/**
 * 新人引导任务处理逻辑
 * 复制自 task-list.vue handleDoingTask 及其依赖方法
 * 与 task-list 保持一致
 */
import { Native, env } from '@hb/NBridge/lib/h5';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import { mapBusinessInfo } from '@/utils/mappers';
import { getTokenAndKey, getTokenAndLocation } from '@/utils/index';
import { getVersion, getSystemCode } from '@/utils/systemCode';
import { jumpEvent } from '@/api/lottery';
import Toast from '@/components/toast';
import { ParamsUtil } from '@/utils/params';
import {
  jumpUrl,
  checkWXInstallation,
  generateUUID,
} from '@/utils/tools';
import {
  openSystemSettingPage,
  mobileAdReplacementWithLink,
  openDeepLinkWithParams,
  platformOpenScheme,
} from '@/pages/welfare/common/bridge';
import { requestTaskMonitoring } from '@/pages/welfare/common/helper';
import { handleMultiTraceTask } from '@/pages/welfare/common/task';
import {
  TrueJumpCrossTasks,
  ORDER_JUMP_CROSS,
  JUMP_TO_WX_RENT,
  WeiboBrowse,
  WhiteList,
  SYSTEM_SETTING_TASKS,
  MARKET_PLAN,
} from '@/pages/welfare/consts';

/**
 * 任务链接参数处理
 */
async function taskLinkParamsHandle(link, task) {
  try {
    const res = await mobileAdReplacementWithLink({ link });
    const { userNewId } = await getTokenAndKey();
    const resLink = ParamsUtil.addParams(res.link, {
      userid: userNewId,
      taskCode: task.taskCode,
    })
      .replace('__REQUESTID__', generateUUID())
      .replace('__TASK_CODE__', task.taskCode)
      .replace('__USER_NEW_ID__', userNewId);
    return resLink;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('任务链接参数处理失败', error);
    return link;
  }
}

/**
 * 处理跨端跳转（ORDER_JUMP_CROSS）
 */
async function handleOrderJumpCross(link, jumpTimer) {
  if (env.isIOS) {
    if ((await getVersion()) >= '6.72.0') {
      platformOpenScheme(link);
    } else if (
      WhiteList.filter((whiteItem) => link.schema && link.schema.includes(whiteItem)).length > 0
    ) {
      window.location.href = link.schema;
      return setTimeout(() => {
        Native.openThirdWeb({ url: link.h5 });
      }, 3000);
    } else {
      Native.openThirdWeb({ url: link.h5 });
    }
  } else if (env.isAndroid) {
    if ((await getVersion()) >= '6.70.0') {
      platformOpenScheme(link);
    } else {
      Native.openThirdWeb({ url: link.h5 });
    }
  }
  return jumpTimer;
}

/**
 * 处理真跨端跳转（TrueJumpCrossTasks）
 */
async function handleTrueJumpCross(link, jumpTimer) {
  if (env.isIOS) {
    if ((await getVersion()) >= '6.72.0') {
      platformOpenScheme(link);
    } else if (
      WhiteList.filter((whiteItem) => link.schema && link.schema.includes(whiteItem)).length > 0
    ) {
      window.location.href = link.schema;
      return setTimeout(() => {
        Native.openThirdWeb({ url: link.h5 });
      }, 3000);
    } else {
      Native.openThirdWeb({ url: link.h5 });
    }
  } else if (env.isAndroid) {
    if ((await getVersion()) >= '6.70.0') {
      platformOpenScheme(link);
    } else {
      Native.openThirdWeb({ url: link.h5 });
    }
  }
  return jumpTimer;
}

/**
 * 处理 taskActivityType === 1 的任务
 */
async function handleActivityTypeOneTask(item, jumpTimer) {
  let channel = 'app';
  if (env.isAliMP || env.isAliMPMonitor) {
    channel = 'ali';
  }
  const { token, adCode, cityCode } = await getTokenAndLocation();
  try {
    let btnLink = item.btnLink[channel];
    if (ORDER_JUMP_CROSS.includes(item.marketPlan)) {
      btnLink = item.btnLink[channel].schema;
    }
    const res = await jumpEvent({
      token,
      cityCode,
      adCode,
      channelId: env.platformCode,
      systemCode: getSystemCode(),
      version: await getVersion(),
      btnLink,
      taskCode: item.taskCode,
      sceneNameEn: 'weekly_cash',
      subSceneNameEn: 'weekly_cash_app',
      browseDuration: item.browseDuration,
    });
    if (res.success) {
      const link = item.btnLink[channel] || item.titleBarUrl;
      if (channel === 'ali') {
        Native.callNative({ method: 'goOperationUrlV2', params: link });
      } else {
        window.onpageshow = null;
        window.onpageshow = () => { window.location.reload(); };
        if (ORDER_JUMP_CROSS.includes(item.marketPlan)) {
          // eslint-disable-next-line no-param-reassign
          jumpTimer = await handleOrderJumpCross(link, jumpTimer);
          return jumpTimer;
        }
        if (env.isEasyBikeApp && env.isIOS && JUMP_TO_WX_RENT.includes(item.marketPlan)) {
          const isWXInstalled = await checkWXInstallation();
          if (!isWXInstalled) {
            Toast.show('请先安装微信');
            return jumpTimer;
          }
        }
        jumpUrl(link);
      }
    }
  } catch (e) {
    Toast.show(e && e.message ? e.message : '任务领取失败');
  }
  return jumpTimer;
}

/**
 * 处理其他类型任务
 */
async function handleOtherTask(item, jumpTimer) {
  let channel = 'app';
  if (env.isAliMP || env.isAliMPMonitor) {
    channel = 'ali';
  }
  const link = item.btnLink[channel] || item.titleBarUrl;
  if (channel === 'ali') {
    Native.callNative({ method: 'goOperationUrlV2', params: link });
  } else if (item.taskActivityType === 8) {
    window.location.href = link;
  } else if (item.marketPlan === 'weibo_browse') {
    Native.openThirdWeb({ url: link });
  } else if (TrueJumpCrossTasks.includes(item.marketPlan)) {
    // eslint-disable-next-line no-param-reassign
    jumpTimer = await handleTrueJumpCross(link, jumpTimer);
  } else {
    jumpUrl(link);
  }
  return jumpTimer;
}

/**
 * 处理进行中的任务跳转
 * 复制自 task-list.handleDoingTask
 * @param {Object} item 任务数据
 * @param {number|null} jumpTimer 跳转定时器引用（用于跨端跳转超时处理）
 * @returns {number|null} 新的 jumpTimer
 */
export default async function handleDoingTask(item, jumpTimer = null) {
  UBT.clickButton({
    pageId: 'Welfare',
    buttonName: 'market_task_clicktodo',
    categoryId: 'market',
    businessInfo: mapBusinessInfo(item),
  });
  // 商业化任务多链接跳转
  if (
    item.businessType === 'commercial'
    && (await getVersion()) >= '6.98.50'
    && item.linkingMode === 3
  ) {
    const { userNewId } = await getTokenAndKey();
    const { app } = item.btnLink;
    const {
      jumpPattern,
      iosAppId = '',
      androidPkgName = '',
      wakePortLink = '',
      link = '',
      userName = '',
      path = '',
      ulk,
      clickTracking = '',
    } = app;
    try {
      Native.callNative({
        classMap: 'hellobike/marketing/openAllAdsURLAndTrack',
        method: 'openAdsUrlWithTracking',
        params: {
          linkType: jumpPattern === 1 ? 'applet' : 'deeplink',
          jumpConfig: {
            iosAppId, androidPkgName, userName, path, wakePortLink, link, ulk,
          },
          trackConfig: {
            userId: userNewId,
            taskCode: item.taskCode,
            trackerUrls: [{ trackerUrl: clickTracking, requestType: 'get' }],
          },
          appendConfig: [
            { replaceKey: '__USER_NEW_ID__', appendKey: 'userId', value: userNewId },
            { replaceKey: '__TASK_CODE__', appendKey: 'taskCode', value: item.taskCode },
          ],
          uuid: generateUUID(),
        },
      });
    } catch (e) {
      Toast.show(e && e.message ? e.message : '网络开小差~');
    }
    return jumpTimer;
  }

  // 商业方案 pp_effct_adtask
  if (item.marketPlan === MARKET_PLAN.PP_EFFECT_ADTASK) {
    const { app } = item.btnLink;
    const { schema, h5 } = app;
    if (h5) {
      const newLink = await taskLinkParamsHandle(h5, item);
      await requestTaskMonitoring(newLink, item);
    }
    if (schema) {
      const httpsRegex = /^https?:\/\/\S+/;
      const newLink = await taskLinkParamsHandle(schema, item);
      if (httpsRegex.test(schema)) {
        Native.openThirdWeb({ url: newLink });
      } else {
        openDeepLinkWithParams({ wakeLink: newLink });
      }
    }
    return jumpTimer;
  }

  // 跳转系统设置页
  if (SYSTEM_SETTING_TASKS.includes(item.marketPlan)) {
    openSystemSettingPage();
    return jumpTimer;
  }

  // 多链路追踪任务
  if (item.linkingMode === 3) {
    handleMultiTraceTask(item);
    return jumpTimer;
  }

  const matchPlan = [...TrueJumpCrossTasks, ...WeiboBrowse].includes(item.marketPlan);
  if (item.taskActivityType === 1 && !matchPlan) {
    return handleActivityTypeOneTask(item, jumpTimer);
  }
  return handleOtherTask(item, jumpTimer);
}
